import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Avatar,
  Divider
} from '@mui/material';
import { Business as BusinessIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { SwalConfig } from '../../utils/swalConfig';
import axios from 'axios';

const EditCustomer = () => {
  const { id } = useParams();
  const [customerData, setCustomerData] = useState({
    _id: '',
    customer_name: '',
    pic_title: '',
    pic_last_name: '',
    pic_position: '',
    billing_address: '',
    address: '',
    id_subdistrict: '',
    id_city: '',
    id_province: '',
    phone: '',
    fax: '',
    mobile: '',
    email: '',
    business_type: '',
    term_of_payment: '',
    year_founded: '',
    id_bu: '',
    max_credit_limit: '',
    tax_no: '',
    bank_account_name: '',
    bank_account_number: '',
    bank_account_nameholder: '',
    bank_account_brench: '',
    is_active: 1,
    internal_pic: '',
  });

  const [buList, setBuList] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetching business units
    const fetchBusinessUnits = async () => {
      try {
        const response = await fetch('https://apidashboard.eurekagroup.id/bu');
        const businessUnits = await response.json();
        setBuList(businessUnits);
      } catch (error) {
        console.error('Error fetching business units:', error);
      }
    };
  
    // Fetching customer data
    const fetchCustomerData = async () => {
      try {
        const response = await fetch(`https://apidashboard.eurekagroup.id/customers/get/${id}`);
        const data = await response.json();
        if (data) {
          setCustomerData({
            ...data,
            date_added: data.date_added ? data.date_added.split('T')[0] : '',
            date_modified: data.date_modified ? data.date_modified.split('T')[0] : '',
            id_bu: data.id_bu ? data.id_bu._id : '', // Assigning the _id of the selected Business Unit
          });
        }
      } catch (error) {
        console.error('Error fetching customer data:', error);
      }
    };
  
    fetchBusinessUnits();
    fetchCustomerData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCustomerData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
  
    const currentDate = new Date().toISOString();
  
    const updatedCustomerData = {
      id: customerData._id,
      customer_name: customerData.customer_name || "",
      pic_title: customerData.pic_title || "",
      pic_last_name: customerData.pic_last_name || "",
      pic_position: customerData.pic_position || "",
      billing_address: customerData.billing_address || "",
      address: customerData.address || "",
      id_subdistrict: customerData.id_subdistrict || "",
      id_city: customerData.id_city || "",
      id_province: customerData.id_province || "",
      phone: customerData.phone || "",
      fax: customerData.fax || "",
      mobile: customerData.mobile || "",
      email: customerData.email || "",
      business_type: customerData.business_type || "",
      term_of_payment: customerData.term_of_payment || "",
      year_founded: customerData.year_founded || "",
      id_bu: customerData.id_bu || "",
      max_credit_limit: customerData.max_credit_limit || "",
      tax_no: customerData.tax_no || "",
      bank_account_name: customerData.bank_account_name || "",
      bank_account_number: customerData.bank_account_number || "",
      bank_account_nameholder: customerData.bank_account_nameholder || "",
      bank_account_brench: customerData.bank_account_brench || "",
      internal_pic: customerData.internal_pic || "",
      is_active: customerData.is_active || 1,
      date_added: currentDate,
      date_modified: currentDate,
    };
  
    const token = localStorage.getItem('token');
  
    try {
      await axios.put(
        `https://apidashboard.eurekagroup.id/customers/edit`, 
        updatedCustomerData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      SwalConfig.success(
        'Berhasil Diperbarui!',
        'Data customer telah berhasil diperbarui.',
        2000
      );
      navigate('/kontak/customer');
    } catch (error) {
      console.error('Error updating customer data:', error);
      SwalConfig.error('Gagal memperbarui customer', 'Terjadi kesalahan saat memperbarui data');
    } finally {
      setLoading(false);
    }
  };  

  const handleBack = () => {
    navigate('/kontak/customer');  // Navigate back to the customer list page
  };

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <BusinessIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Edit Customer
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Perbarui informasi customer yang sudah ada
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Button 
              variant="outlined" 
              color="secondary" 
              startIcon={<ArrowBackIcon />} 
              onClick={handleBack}
              className="hover:scale-105 transition-all duration-300"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Kembali
            </Button>
          }
          sx={{ pb: 2 }}
        />
        <Divider />
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Informasi Dasar */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                  📋 Informasi Dasar
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Nama Customer"
                  name="customer_name"
                  value={customerData.customer_name}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  required 
                  fullWidth 
                  label="Tipe Bisnis" 
                  name="business_type" 
                  value={customerData.business_type} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  required 
                  fullWidth 
                  label="Tahun Didirikan" 
                  name="year_founded" 
                  type="number" 
                  value={customerData.year_founded} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  select
                  fullWidth
                  label="Business Unit"
                  name="id_bu"
                  value={customerData.id_bu || ''}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                >
                  {buList.map((bu) => (
                    <MenuItem key={bu._id} value={bu._id}>
                      {bu.bu_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Informasi PIC */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2, mt: 2 }}>
                  👤 Informasi PIC (Person In Charge)
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  required 
                  fullWidth 
                  label="Gelar PIC" 
                  name="pic_title" 
                  value={customerData.pic_title} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  required 
                  fullWidth 
                  label="Nama Belakang PIC" 
                  name="pic_last_name" 
                  value={customerData.pic_last_name} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  required 
                  fullWidth 
                  label="Posisi PIC" 
                  name="pic_position" 
                  value={customerData.pic_position} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Internal PIC" 
                  name="internal_pic" 
                  value={customerData.internal_pic} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Status"
                  name="is_active"
                  select
                  value={customerData.is_active}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                >
                  <MenuItem value={1}>Aktif</MenuItem>
                  <MenuItem value={0}>Tidak Aktif</MenuItem>
                </TextField>
              </Grid>

              {/* Alamat */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2, mt: 2 }}>
                  🏢 Informasi Alamat
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  required 
                  fullWidth 
                  label="Alamat Lengkap" 
                  name="address" 
                  value={customerData.address} 
                  onChange={handleChange}
                  multiline
                  rows={3}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  required 
                  fullWidth 
                  label="Alamat Penagihan" 
                  name="billing_address" 
                  value={customerData.billing_address} 
                  onChange={handleChange}
                  multiline
                  rows={2}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  fullWidth 
                  label="ID Kecamatan" 
                  name="id_subdistrict" 
                  value={customerData.id_subdistrict} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  fullWidth 
                  label="ID Kota" 
                  name="id_city" 
                  value={customerData.id_city} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  fullWidth 
                  label="ID Provinsi" 
                  name="id_province" 
                  value={customerData.id_province} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>

              {/* Kontak */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2, mt: 2 }}>
                  📞 Informasi Kontak
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  required 
                  fullWidth 
                  label="Telepon" 
                  name="phone" 
                  value={customerData.phone} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  fullWidth 
                  label="Fax" 
                  name="fax" 
                  value={customerData.fax} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField 
                  required 
                  fullWidth 
                  label="Mobile" 
                  name="mobile" 
                  value={customerData.mobile} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  required 
                  fullWidth 
                  label="Email" 
                  name="email" 
                  type="email"
                  value={customerData.email} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Term of Payment" 
                  name="term_of_payment" 
                  value={customerData.term_of_payment} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>

              {/* Informasi Keuangan */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2, mt: 2 }}>
                  💰 Informasi Keuangan
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Max Credit Limit" 
                  name="max_credit_limit" 
                  type="number" 
                  value={customerData.max_credit_limit} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Tax No" 
                  name="tax_no" 
                  value={customerData.tax_no} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>

              {/* Informasi Bank */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2, mt: 2 }}>
                  🏦 Informasi Bank
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Nama Bank" 
                  name="bank_account_name" 
                  value={customerData.bank_account_name} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Nomor Rekening" 
                  name="bank_account_number" 
                  value={customerData.bank_account_number} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Nama Pemegang Rekening" 
                  name="bank_account_nameholder" 
                  value={customerData.bank_account_nameholder} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth 
                  label="Cabang Bank" 
                  name="bank_account_brench" 
                  value={customerData.bank_account_brench} 
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Grid>

              {/* Tombol Aksi */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{ 
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Batal
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                    sx={{ 
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {loading ? 'Memperbarui...' : '✨ Perbarui Customer'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditCustomer;
